import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import moment from 'moment';

import { PencilIcon } from '../../../../../assets';
import { EditableCollapse, FormColorfulSelect, FormInput, FormNewSelect } from '../../../../../components';
import { AppointmentCategoryEnum, appointmentCategories } from '../../../../../entities/appointments';
import { USADateFormat, apiDateFormat } from '../../../../../utils';
import { useStyles } from './assets';
import { LeadFormProps, LeadFormValues, checkTargetDate, leadFormValidationSchema } from './model';

export const LeadForm = memo(({ lead, categories, lostReasons, separator = true, onSubmit }: LeadFormProps) => {
  const styles = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true,
  });

  const isFirstRenderRef = useRef(true);

  const [initialLead] = useState(lead);
  const [isTargetDateEditing, setIsTargetDateEditing] = useState(false);
  const [isReasonEditing, setIsReasonEditing] = useState(false);
  const [isEstimateValueEditing, setIsEstimateValueEditing] = useState(false);

  const defaultValues = useMemo(
    () => ({
      category: initialLead.category.id,
      targetDate: checkTargetDate(initialLead.targetDate) ? moment(initialLead.targetDate).format(USADateFormat) : '',
      reason: initialLead.noSaleReason.id
        ? {
            value: initialLead.noSaleReason.id,
            label: initialLead.noSaleReason.name,
          }
        : null,
      lowEstimate: initialLead.estimatedValue.low ? Number(initialLead.estimatedValue.low) : 0,
      highEstimate: initialLead.estimatedValue.high ? Number(initialLead.estimatedValue.high) : 0,
    }),
    [initialLead],
  );

  const { control, formState, watch, setValue, trigger, getValues, clearErrors } = useForm<LeadFormValues>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(leadFormValidationSchema),
  });

  const [category, targetDate, reason, lowEstimate, highEstimate] = watch([
    'category',
    'targetDate',
    'reason',
    'lowEstimate',
    'highEstimate',
  ]);

  const isEditing = useMemo(
    () => isTargetDateEditing || isReasonEditing || isEstimateValueEditing,
    [isTargetDateEditing, isReasonEditing, isEstimateValueEditing],
  );

  const categoryOptions = useMemo(() => categories.filter(ctgr => ctgr.id in AppointmentCategoryEnum), [categories]);

  const lostReasonOptions = useMemo(
    () =>
      lostReasons
        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
        .map(item => ({
          value: item.id,
          label: item.friendlyName,
        })),
    [lostReasons],
  );

  const handleEdit = useCallback((type: 'date' | 'reason' | 'value-estimate') => {
    setIsTargetDateEditing(false);
    setIsReasonEditing(false);
    setIsEstimateValueEditing(false);

    switch (type) {
      case 'date': {
        setIsTargetDateEditing(true);
        break;
      }

      case 'reason': {
        setIsReasonEditing(true);
        break;
      }

      case 'value-estimate': {
        setIsEstimateValueEditing(true);
        break;
      }
    }
  }, []);

  const handleClickAway = useCallback(
    (type: 'date' | 'reason' | 'value-estimate') => {
      switch (type) {
        case 'date': {
          if (isTargetDateEditing) {
            if (formState.errors.targetDate) {
              setValue('targetDate', defaultValues.targetDate);
              setTimeout(() => {
                clearErrors('targetDate');
              }, 0);
            }

            setIsTargetDateEditing(false);
          }

          break;
        }

        case 'reason': {
          if (isReasonEditing) {
            setIsReasonEditing(false);
          }

          break;
        }

        case 'value-estimate': {
          if (isEstimateValueEditing) {
            setIsEstimateValueEditing(false);
          }

          if (formState.errors.lowEstimate) {
            setValue('lowEstimate', defaultValues.lowEstimate);
            setTimeout(() => {
              clearErrors('lowEstimate');
            }, 0);
          }

          if (formState.errors.highEstimate) {
            setValue('highEstimate', defaultValues.highEstimate);
            setTimeout(() => {
              clearErrors('highEstimate');
            }, 0);
          }

          break;
        }
      }
    },
    [
      defaultValues,
      isEstimateValueEditing,
      isReasonEditing,
      isTargetDateEditing,
      formState.errors.targetDate,
      formState.errors.lowEstimate,
      formState.errors.highEstimate,
      setValue,
      clearErrors,
    ],
  );

  const submitForm = useCallback(() => {
    const currentValues = getValues();

    const currentCategory = currentValues.category;
    const currentTargetDate = currentValues.targetDate;
    const currentReason = currentValues.reason;
    const currentLowEstimate = currentValues.lowEstimate;
    const currentHighEstimate = currentValues.highEstimate;

    onSubmit({
      category: !formState.errors.category && currentCategory ? currentCategory : initialLead.category.id,
      targetDate: !formState.errors.targetDate
        ? moment(currentTargetDate, ['MMDDYYYY', USADateFormat]).format(apiDateFormat)
        : initialLead.targetDate,
      reason: !formState.errors.reason && currentReason ? currentReason : defaultValues.reason,
      lowEstimate: !formState.errors.lowEstimate ? currentLowEstimate : initialLead.estimatedValue.low,
      highEstimate: !formState.errors.highEstimate ? currentHighEstimate : initialLead.estimatedValue.high,
    });
  }, [formState.errors, defaultValues, initialLead, onSubmit, getValues]);

  useEffect(() => {
    trigger('highEstimate');
  }, [lowEstimate]);

  useEffect(() => {
    trigger('lowEstimate');
  }, [highEstimate]);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    if (isEditing) {
      return;
    }

    submitForm();
  }, [isEditing, category, targetDate, reason, lowEstimate, highEstimate]);

  return (
    <>
      {separator && <Divider className={styles.divider} />}

      <Grid container spacing={2} component="form">
        <Grid item xs={isMobile ? 6 : undefined} className={clsx(!isMobile && 'gridItem-35')}>
          <Typography component="div" variant="subtitle1" color="secondary">
            Category
          </Typography>

          <FormColorfulSelect
            control={control}
            name="category"
            size="small"
            fitContent
            customPallete={appointmentCategories[category]?.color}
            options={categoryOptions}
            classes={{ root: styles.categorySelectRoot }}
          />
        </Grid>

        <Grid item xs={isMobile ? 6 : undefined} className={clsx(!isMobile && 'gridItem-35')}>
          <EditableCollapse
            isEditing={isTargetDateEditing}
            onClickAway={() => handleClickAway('date')}
            displayContent={
              <>
                <Typography component="div" variant="subtitle1" color="secondary">
                  Target date
                </Typography>

                <div className={styles.contentItem}>
                  {checkTargetDate(targetDate, ['MMDDYYYY', USADateFormat]) ? (
                    <Typography component="div">
                      {moment(targetDate, ['MMDDYYYY', USADateFormat]).format(USADateFormat)}
                    </Typography>
                  ) : (
                    '-'
                  )}

                  <IconButton
                    className="iconButtonMedium"
                    onClick={e => {
                      e.stopPropagation();
                      handleEdit('date');
                    }}
                  >
                    <PencilIcon fontSize={18} />
                  </IconButton>
                </div>
              </>
            }
            editContent={
              <div>
                <FormInput
                  control={control}
                  name="targetDate"
                  label="Select a date"
                  mask="date"
                  dateFormat="MM/DD/YYYY"
                  minDate={moment({ year: 2000, month: 0, day: 2 })}
                  calendarAdornment
                  className={styles.editingInput}
                />
              </div>
            }
          />
        </Grid>

        <Grid item xs={6} md={5}>
          <EditableCollapse
            isEditing={isReasonEditing}
            onClickAway={() => handleClickAway('reason')}
            displayContent={
              <>
                <Typography component="div" variant="subtitle1" color="secondary">
                  Reason not booked
                </Typography>

                <div className={styles.contentItem}>
                  <Typography component="div" className={styles.noSaleReason}>
                    {reason?.label || '-'}
                  </Typography>

                  <IconButton
                    className="iconButtonMedium"
                    onClick={e => {
                      e.stopPropagation();
                      handleEdit('reason');
                    }}
                  >
                    <PencilIcon fontSize={18} />
                  </IconButton>
                </div>
              </>
            }
            editContent={
              <div>
                <FormNewSelect
                  control={control}
                  name="reason"
                  label="Reason not booked"
                  virtualize={false}
                  options={lostReasonOptions}
                />
              </div>
            }
          />
        </Grid>

        <Grid item xs={6} md={12}>
          <EditableCollapse
            isEditing={isEstimateValueEditing}
            onClickAway={() => handleClickAway('value-estimate')}
            displayContent={
              <>
                <Typography component="div" variant="subtitle1" color="secondary">
                  Value estimate
                </Typography>

                <div className={styles.contentItem}>
                  <Typography component="div">
                    ${lowEstimate} - {highEstimate}
                  </Typography>

                  <IconButton
                    className="iconButtonMedium"
                    onClick={e => {
                      e.stopPropagation();
                      handleEdit('value-estimate');
                    }}
                  >
                    <PencilIcon fontSize={18} />
                  </IconButton>
                </div>
              </>
            }
            editContent={
              <div className={clsx(styles.contentItem, styles.alignStart)}>
                <FormInput
                  control={control}
                  name="lowEstimate"
                  label="Low range estimate"
                  mask="positive-two-decimal"
                  className={styles.editingInput}
                />

                <FormInput
                  control={control}
                  name="highEstimate"
                  label="High range estimate"
                  mask="positive-two-decimal"
                  className={styles.editingInput}
                />
              </div>
            }
          />
        </Grid>
      </Grid>
    </>
  );
});
